import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaBook, FaQuestionCircle, FaHeadset, FaEnvelope, FaArrowRight, FaStar, FaCheck, FaTimes } from 'react-icons/fa';
import ContactForm from '../components/ContactForm';

const HelpCenter: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  const helpCategories = [
    {
      title: 'Getting Started',
      icon: '🚀',
      items: [
        { question: 'How do I create an account?', answer: 'Click the "Sign Up" button in the top navigation and follow the registration process. You can also use your Google account for quick registration.' },
        { question: 'How do I start learning?', answer: 'After logging in, visit the Dashboard to see your learning progress and recommended lessons. You can also explore Pathways for structured learning.' },
        { question: 'What are Pathways?', answer: 'Pathways are structured learning journeys that guide you through Farsi vocabulary and grammar in a logical progression.' }
      ]
    },
    {
      title: 'Learning Tools',
      icon: '📚',
      items: [
        { question: 'How do Flashcards work?', answer: 'Flashcards help you memorize vocabulary. Click through cards, mark them as known or unknown, and review them regularly for better retention.' },
        { question: 'How do I take Quizzes?', answer: 'Quizzes test your knowledge of vocabulary and grammar. You can retake quizzes multiple times to improve your score.' },
        { question: 'What is Word of the Day?', answer: 'Word of the Day introduces you to a new Farsi word daily, helping you build vocabulary gradually.' }
      ]
    },
    {
      title: 'Cultural Content',
      icon: '🏛️',
      items: [
        { question: 'What cultural content is available?', answer: 'Explore Persian poetry, recipes, holidays, and Tehran lessons to immerse yourself in Persian culture while learning the language.' },
        { question: 'How do Hafez lessons work?', answer: 'Hafez lessons introduce you to classical Persian poetry, helping you understand cultural context and advanced vocabulary.' }
      ]
    },
    {
      title: 'Account & Subscription',
      icon: '💎',
      items: [
        { question: 'How do I manage my subscription?', answer: 'Visit your Profile page to view and manage your subscription settings, billing information, and account preferences.' },
        { question: 'What features are included in the free plan?', answer: 'Free users can access basic flashcards, limited quizzes, and some cultural content. Premium features require a subscription.' },
        { question: 'How do I cancel my subscription?', answer: 'You can cancel your subscription anytime from your Profile page under the subscription settings.' }
      ]
    },
    {
      title: 'Technical Support',
      icon: '🔧',
      items: [
        { question: 'The app is not loading properly', answer: 'Try refreshing your browser or clearing your cache. If the problem persists, contact our support team.' },
        { question: 'I forgot my password', answer: 'Use the "Forgot Password" link on the login page to reset your password via email.' },
        { question: 'How do I report a bug?', answer: 'Use the Contact Us form or email us directly with details about the issue you encountered.' }
      ]
    }
  ];

  const filteredCategories = helpCategories.filter(category =>
    category.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.items.some(item =>
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div className="font-sans bg-gradient-to-br from-white via-blue-50 to-indigo-50 text-gray-800 min-h-screen" dir="ltr">
      {/* Header Section */}
      <section className="container mx-auto px-6 py-12 md:py-20 relative">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <FaQuestionCircle className="text-4xl text-indigo-500 mr-3" />
            <h1 className="font-elegant text-4xl md:text-5xl lg:text-6xl font-bold text-indigo-800 leading-tight">
              Help Center
            </h1>
          </div>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Find answers to common questions and get the most out of your Farsi learning journey
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search for help articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white border border-indigo-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm"
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Link
            to="/contact-us"
            className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300 group"
          >
            <div className="h-32 bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center">
              <FaHeadset className="text-4xl text-indigo-600" />
            </div>
            <div className="p-6">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Contact Support</h3>
              <p className="text-gray-600 text-sm">Get personalized help from our team</p>
            </div>
          </Link>

          <Link
            to="/faq"
            className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300 group"
          >
            <div className="h-32 bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
              <FaQuestionCircle className="text-4xl text-purple-600" />
            </div>
            <div className="p-6">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">FAQ</h3>
              <p className="text-gray-600 text-sm">Quick answers to common questions</p>
            </div>
          </Link>

          <button
            onClick={() => setIsContactModalOpen(true)}
            className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300 group w-full text-left"
          >
            <div className="h-32 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <FaEnvelope className="text-4xl text-blue-600" />
            </div>
            <div className="p-6">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Email Us</h3>
              <p className="text-gray-600 text-sm">Send us a detailed message</p>
            </div>
          </button>
        </div>
      </section>

      {/* Help Categories Section */}
      <section className="container mx-auto px-6 py-12 bg-gradient-to-b from-indigo-50 to-white rounded-t-3xl -mt-12">
        <div className="text-center mb-12">
          <h2 className="font-elegant text-3xl md:text-4xl font-bold text-indigo-800 mb-4">Help Categories</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">Browse through our organized help sections to find the information you need.</p>
        </div>
        
        <div className="space-y-8">
          {filteredCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-white rounded-xl shadow-md overflow-hidden card-hover hover:shadow-xl transition-all duration-300">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 border-b border-indigo-100">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">{category.icon}</span>
                  <h2 className="font-elegant text-2xl font-bold text-indigo-800">{category.title}</h2>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-6">
                  {category.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="border-l-4 border-indigo-300 pl-6 hover:border-indigo-500 transition-colors">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                        <FaStar className="text-indigo-400 mr-2 text-sm" />
                        {item.question}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Additional Resources Section */}
      <section className="container mx-auto px-6 py-16">
        <div className="bg-gradient-to-br from-indigo-100 to-indigo-50 rounded-3xl p-8 md:p-12 shadow-lg">
          <div className="text-center mb-8">
            <h2 className="font-elegant text-3xl font-bold text-indigo-800 mb-4">Additional Resources</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Explore more learning materials and community resources to enhance your Farsi learning experience.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-3 bg-indigo-100 rounded-full">
                  <FaBook className="text-indigo-600 text-xl" />
                </div>
                <h3 className="text-lg font-semibold text-indigo-800">Learning Guide</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Comprehensive guide to effective Farsi learning strategies and techniques.
              </p>
              <Link
                to="/learning-guide"
                className="text-indigo-600 hover:text-indigo-800 font-medium text-sm transition-colors flex items-center"
              >
                Read Guide <FaArrowRight className="ml-2 text-sm" />
              </Link>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-3 bg-purple-100 rounded-full">
                  <FaQuestionCircle className="text-purple-600 text-xl" />
                </div>
                <h3 className="text-lg font-semibold text-indigo-800">Community Forum</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Connect with other learners, ask questions, and share your progress.
              </p>
              <Link
                to="/forum"
                className="text-indigo-600 hover:text-indigo-800 font-medium text-sm transition-colors flex items-center"
              >
                Join Discussion <FaArrowRight className="ml-2 text-sm" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Still Need Help Section */}
      <section className="container mx-auto px-6 py-16 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-elegant text-3xl md:text-4xl font-bold text-indigo-800 mb-6">Still Need Help?</h2>
          <p className="text-gray-600 mb-8">
            Our support team is here to help you succeed in your Farsi learning journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact-us"
              className="inline-block px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full font-medium shadow-lg hover:shadow-indigo-200 transition-all hover:scale-105"
            >
              Contact Support
            </Link>
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="inline-block px-8 py-3 border-2 border-indigo-400 text-indigo-600 rounded-full font-medium hover:bg-indigo-50 transition-all"
            >
              Email Us
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setIsContactModalOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <FaTimes className="text-gray-600 dark:text-gray-300" />
            </button>
            <ContactForm className="m-0" />
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          .card-hover {
            transition: all 0.3s ease;
          }
          .card-hover:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
        `
      }} />
    </div>
  );
};

export default HelpCenter; 